// pages/category/[slug]/CategoryBlog.js
"use client";

import Breadcrumbs from "@/components/Breadcrumbs";
import { getSortedBlogs } from "@/lib/mdxBlogs";
import { Pagination } from "flowbite-react";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";

const numberOfPages = 24;

const CategoryBlog = ({ slug }) => {
  const [currentBlogs, setCurrentBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      if (slug) {
        try {
          setLoading(true);
          const { blogs: data, totalCount } = await getSortedBlogs(
            currentPage,
            numberOfPages,
            "",
            slug
          );
          setCurrentBlogs(data);
          setTotalPages(Math.ceil(totalCount / numberOfPages));
          setLoading(false);
        } catch (err) {
          console.error(err);
          setError("Failed to load blogs.");
          setLoading(false);
        }
      }
    };

    fetchBlogs();
  }, [currentPage, slug]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  const onPageChange = (page) => setCurrentPage(page);

  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "All Categories", href: "/category" },
  ];

  return (
    <div>
      <section className="title-heading py-10 pt-36 relative">
        <Image
          src="/images/blog/blogging.jpg"
          alt="dynamic image"
          fill
          sizes="100vw"
          className="-z-20 absolute object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 -z-10"></div>
        <div className="max-w-screen-xl mx-5 lg:mx-auto">
          <div className="grid grid-cols-1 gap-3 items-center justify-between text-center">
            <div>
              <div className="flex items-center justify-center ali space-x-6 py-2 mx-auto font-sans">
                <Breadcrumbs breadcrumbs={breadcrumbs} className="text-white" />
              </div>
              <h1 className="text-white font-bold mb-0 text-6xl font-sans">
                Category: {decodeURIComponent(slug) || "Loading..."}
              </h1>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-screen-xl mx-5 md:mx-auto py-10">
        <div className="flex justify-center mb-6">
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="p-2 border border-gray-300 rounded-lg w-full max-w-md"
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {currentBlogs.map(
            ({
              id,
              slug,
              date,
              title,
              featuredImage,
              excerpt,
              author,
              category,
            }) => (
              <div key={id} className="bg-gray-200 rounded-lg overflow-hidden">
                <div className="relative w-full h-56 overflow-hidden">
                  <Link href={`/blog/${slug}`}>
                    <Image
                      src={featuredImage || "/images/blog/default.jpg"}
                      alt={title}
                      width={2048}
                      height={1075}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 1s ease-in-out",
                        hover: { transform: "scale(1.1)" },
                      }}
                    />
                  </Link>
                </div>

                <div className="px-4 my-8 flex flex-col">
                  <div className="pb-4 text-gray-800 font-bold font-sans uppercase text-base">
                    {Array.isArray(category) ? (
                      category.map((cat, index) => (
                        <React.Fragment key={index}>
                          <Link
                            href={`/blog/category/${encodeURIComponent(cat)}`}
                            className="text-colorGrey-800 uppercase"
                          >
                            {cat}
                          </Link>
                          {", "}
                        </React.Fragment>
                      ))
                    ) : (
                      <Link href={`/blog/category/Uncategorized`}>
                        Uncategorized
                      </Link>
                    )}
                  </div>
                  <h2 className="text-2xl font-semibold">
                    <Link
                      className="hover:text-red-800 text-black"
                      href={`/blog/${slug}`}
                    >
                      {title}
                    </Link>
                  </h2>
                  <div className="text-lg">{excerpt}</div>
                </div>
              </div>
            )
          )}
        </div>
        <div className="flex justify-center my-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CategoryBlog;
